export type OfflineStatus =
  | "unsupported"
  | "installing"
  | "ready"
  | "update"
  | "error";

type Listener = (status: OfflineStatus) => void;

const listeners = new Set<Listener>();
let status: OfflineStatus =
  typeof navigator !== "undefined" && "serviceWorker" in navigator
    ? navigator.serviceWorker.controller
      ? "ready"
      : "installing"
    : "unsupported";

function setStatus(next: OfflineStatus): void {
  if (status === next) return;
  status = next;
  if (
    next === "ready" &&
    typeof performance !== "undefined" &&
    performance.getEntriesByName("lexibit-offline-ready").length === 0
  ) {
    performance.mark("lexibit-offline-ready");
  }
  listeners.forEach((listener) => listener(status));
}

export function getOfflineStatus(): OfflineStatus {
  return status;
}

export function publishOfflineStatus(
  next: Exclude<OfflineStatus, "ready">,
): void {
  setStatus(next);
}

export function markOfflineReady(
  container: ServiceWorkerContainer = navigator.serviceWorker,
): void {
  setStatus(container.controller ? "ready" : "installing");
}

export function observeServiceWorker(
  container: ServiceWorkerContainer,
): () => void {
  markOfflineReady(container);
  const onControllerChange = () => markOfflineReady(container);
  container.addEventListener("controllerchange", onControllerChange);
  return () => container.removeEventListener("controllerchange", onControllerChange);
}

export function subscribeOfflineStatus(listener: Listener): () => void {
  listeners.add(listener);
  listener(status);
  return () => listeners.delete(listener);
}
